import { Navigate, Route, RouterProvider, Routes, createHashRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Constants } from '../utils';
import AdminLayout from './layout-admin';
import SchoolLayout from './layout-school';
import TeacherLayout from './layout-teacher';
import FreelanceTeacherLayout from './layout-freelance-teacher';

// Auth
import Login from '../screens/auth/login';
import Logout from '../screens/auth/logout';
import Register from '../screens/auth/register';
import RegisterSchool from '../screens/auth/register-school';
import RegisterTeacher from '../screens/auth/register-teacher';
import Recover from '../screens/auth/recover-password';
import RequirePaidPlan from './required-plan';

// Admin
import AdminHome from '../screens/admin/home/home';
import AdminPricing from '../screens/admin/pricing/pricing';
import AdminUsersReports from '../screens/admin/reports/users/reports';
import AdminPaymentsReports from '../screens/admin/reports/payments/reports';
import AdminSettingsAccounts from '../screens/admin/settings/payment-methods-info/payment-methods-info';

// School
import SchoolHome from '../screens/school/home/home';
import SchoolUsers from '../screens/school/users/users';
import SchoolGroups from '../screens/school/grouping/grouping';
import SchoolStudents from '../screens/school/students/students';
import SchoolSchedules from '../screens/school/schedules/schedules';
import SchoolAssignments from '../screens/school/assignments/assignments';
import SchoolAssignmentsCreate from '../screens/school/assignments/assignments-create';
import SchoolReportsStudents from '../screens/school/reports/reports-students';
import SchoolProfile from '../screens/school/settings/school-settings';
import SchoolPayments from '../screens/school/payments/payments';
import SchoolSchoolYears from '../screens/school/settings/school-years';
import SchoolSettingsScales from '../screens/school/settings/scales/scales';


// Teacher
import TeacherHome from '../screens/teacher/home/home';
import TeacherGrouping from '../screens/teacher/grouping/grouping';
import TeacherStudents from '../screens/teacher/students/students';
import TeacherClassManagement from '../screens/teacher/class-management/class-management';
import TeacherClassManagementCreate from '../screens/teacher/class-management/class-management-create';
import TeacherStudentAssistence from '../screens/teacher/student-follow/assistance/assistance';
import TeacherStudentQualifications from '../screens/teacher/student-follow/qualifications/qualifications';
import TeacherStudentConduct from '../screens/teacher/student-follow/conduct/conduct';
import TeacherStudentAssitenceCreate from '../screens/teacher/student-follow/assistance/create-assistence';
import TeacheStudentrQualificationsCreate from '../screens/teacher/student-follow/qualifications/create-qualifications';
import TeacherStudentConductCreate from '../screens/teacher/student-follow/conduct/conduct-create';
import TeacherChat from '../screens/teacher/chat/chat-screen';
import TeacherStudentConductView from '../screens/teacher/student-follow/conduct/condudct-view';
import TeacherPayments from '../screens/teacher/payments/payments';
import TeacherSchoolYears from '../screens/teacher/settings/school-years';
import TeacherSettingsScales from '../screens/teacher/settings/scales/scales';
import TeacherNotifications from '../screens/teacher/notifications/notifications';

const getUserRouter = (user) => {
  /* Rutas para el admin */
  if (user?.level_id === Constants.USER.LEVELS.ADMIN) {
    return createHashRouter([
      { path: '/logout', element: <Logout /> },
      { index: true, path: '*', Component: AdminRoutes },
    ]);
  }


  /* Rutas para los profesores */
  if (user?.level_id === Constants.USER.LEVELS.PROFESSOR) {

    /* Profesores independientes */
    if (user?.school_id === null) {
      return createHashRouter([
        { path: '/logout', element: <Logout /> },
        { index: true, path: '*', Component: FreelanceTeachersRouters },
      ]);
    }

    /* Profesores de colegios */
    return createHashRouter([
      { path: '/logout', element: <Logout /> },
      { index: true, path: '*', Component: TeacherRoutes }
    ]);
  }

  /* Rutas para los colegios */
  if (user?.level_id === Constants.USER.LEVELS.SCHOOL) {
    return createHashRouter([
      { path: '/logout', element: <Logout /> },
      { index: true, path: '*', Component: SchoolRoutes },
    ]);
  }

  /* Rutas generales */
  return createHashRouter([
    { index: true, path: '/login', element: <Login /> },
    { path: '/register', element: <Register /> },
    { path: '/register/school', element: <RegisterSchool /> },
    { path: '/register/teacher', element: <RegisterTeacher /> },
    { path: '/recover/:code', element: <Recover /> },
    { path: '*', element: <Navigate to="/login" replace /> }
  ]);
};

const Router = () => {
  const user = useSelector(state => state.user);
  return <RouterProvider router={getUserRouter(user)} />
}

const AdminRoutes = () => (
  <Routes>
    <Route element={<AdminLayout />}>
      <Route path="/" element={<AdminHome />} />
      <Route path="/pricing" element={<AdminPricing />} />
      <Route path="/reports/users" element={<AdminUsersReports />} />
      <Route path="/reports/payments" element={<AdminPaymentsReports />} />
      <Route path="/settings/payment-methods/info" element={<AdminSettingsAccounts />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const SchoolRoutes = () => (
  <Routes>
    <Route element={<SchoolLayout />}>
      <Route path="/payments" element={<SchoolPayments />} />
      <Route
        path="*"
        element={
          <RequirePaidPlan>
            <Routes>
              <Route path="/" element={<SchoolHome />} />
              <Route path="/users" element={<SchoolUsers />} />
              <Route path="/groups" element={<SchoolGroups />} />
              <Route path="/students" element={<SchoolStudents />} />
              <Route path="/schedules" element={<SchoolSchedules />} />
              <Route path="/assignments" element={<SchoolAssignments />} />
              <Route path="/assignments/create" element={<SchoolAssignmentsCreate />} />
              <Route path="/reports/students" element={<SchoolReportsStudents />} />
              <Route path="/settings/profile" element={<SchoolProfile />} />
              <Route path="/settings/scales" element={<SchoolSettingsScales />} />
              <Route path="/school-years" element={<SchoolSchoolYears />} />
              {/* Puedes seguir agregando más rutas */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </RequirePaidPlan>
        }
      />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const TeacherRoutes = () => (
  <Routes>
    <Route element={<TeacherLayout />}>
      <Route path="/" element={<TeacherHome />} />
      <Route path="/students" element={<TeacherStudents />} />
      <Route path="/grouping" element={<TeacherGrouping />} />
      <Route path="/class-management" element={<TeacherClassManagement />} />
      <Route path="/class-management/create" element={<TeacherClassManagementCreate />} />
      <Route path="/assistance" element={<TeacherStudentAssistence />} />
      <Route path="/assistance/create" element={<TeacherStudentAssitenceCreate />} />
      <Route path="/qualifications" element={<TeacherStudentQualifications />} />
      <Route path="/qualifications/create" element={<TeacheStudentrQualificationsCreate />} />
      <Route path="/conduct" element={<TeacherStudentConduct />} />
      <Route path="/conduct/create" element={<TeacherStudentConductCreate />} />
      <Route path="/conduct/view" element={<TeacherStudentConductView />} />
      <Route path="/chat" element={<TeacherChat />} />
      <Route path="/notifications" element={<TeacherNotifications />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const FreelanceTeachersRouters = () => (
  <Routes>
    <Route element={<FreelanceTeacherLayout />}>
      <Route path="/payments" element={<TeacherPayments />} />
      <Route
        path="*"
        element={
          <RequirePaidPlan>
            <Routes>
              <Route path="/" element={<TeacherHome />} />
              <Route path="/students" element={<TeacherStudents />} />
              <Route path="/grouping" element={<TeacherGrouping />} />
              <Route path="/class-management" element={<TeacherClassManagement />} />
              <Route path="/class-management/create" element={<TeacherClassManagementCreate />} />
              <Route path="/assistance" element={<TeacherStudentAssistence />} />
              <Route path="/assistance/create" element={<TeacherStudentAssitenceCreate />} />
              <Route path="/qualifications" element={<TeacherStudentQualifications />} />
              <Route path="/qualifications/create" element={<TeacheStudentrQualificationsCreate />} />
              <Route path="/conduct" element={<TeacherStudentConduct />} />
              <Route path="/conduct/create" element={<TeacherStudentConductCreate />} />
              <Route path="/conduct/view" element={<TeacherStudentConductView />} />
              <Route path="/chat" element={<TeacherChat />} />
              <Route path="/school-years" element={<TeacherSchoolYears />} />
              <Route path="/settings/scales" element={<TeacherSettingsScales />} />
              <Route path="/notifications" element={<TeacherNotifications />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </RequirePaidPlan>
        }
      />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

export default Router;